import * as React from 'react';
import {useEffect, useState} from 'react';
import {useStyletron} from "baseui";
import {Frame} from "../z40-uicomp/frame";
import {HeadingSmall, LabelSmall, LabelXSmall, ParagraphSmall} from "baseui/typography";
import {ChevronDown} from "baseui/icon";
import {Centered} from "../z40-uicomp/layout";
import {
    CenterSpinner,
    getHeader,
    headerKeys,
    translate,
    useAuth,
    useEvent,
    useLazyCommand,
    useLazyQuery,
    useLogin
} from "@soap/modules";
// Educational imagery for pricing plans
import starterAccess from '../z30-images/starter-access.jpg';
import familyPlan from '../z30-images/family-plan.jpg';
import tutorPlan from '../z30-images/tutor-plan.jpg';
import buildYourOwn from '../z30-images/build-your-own.jpg';
import {Input} from "baseui/input";
import {Button} from "baseui/button";
import {LineItem, ThinLineItem} from "../z40-uicomp/lineItem";
import {ActionButton} from "../z40-uicomp/actionButton";
import {Select, SIZE} from "baseui/select";
import {PricingPane} from "./pricingPane";
import {appendTrialPromoToBeginOrder, getSessionTrialDisplayMonths} from "../constants/trialPromoSession";
import {useEnsurePaddleSetup} from "../hooks/useEnsurePaddleSetup";
import {useFormatDisplayDate} from "../utils/useFormatDisplayDate";
import {Roles} from "../hooks/useEnumerations";
import {CurrencySelector} from "./CurrencySelector";
import {PlanImage} from "./PlanImage";
import {PriceDisplay} from "./PriceDisplay";
import {InfoTooltip} from "../z70-shadcn/components/InfoTooltip.jsx";

const toMoney = function (price) {
    return Number.isInteger(price) ? price : price.toFixed(2);
}

function pluralize(count, singular, plural) {
    return count === 1 ? singular : plural;
}

function NumberInputWithButtons({value, min, max, step, onChange, size = SIZE.compact, disabled = false}) {
    const [css, theme] = useStyletron();
    const [inputValue, setInputValue] = React.useState(value.toString());
    
    React.useEffect(() => {
        setInputValue(value.toString());
    }, [value]);
    
    const handleDecrement = () => {
        if (disabled) return;
        const newValue = Math.max(min, value - step);
        onChange(newValue);
        setInputValue(newValue.toString());
    };
    
    const handleIncrement = () => {
        if (disabled) return;
        const newValue = Math.min(max, value + step);
        onChange(newValue);
        setInputValue(newValue.toString());
    };
    
    const handleBlur = () => {
        if (disabled) return;
        const numValue = parseInt(inputValue, 10);
        if (isNaN(numValue)) {
            // If invalid, revert to current value
            setInputValue(value.toString());
        } else if (numValue < min) {
            // If below min, revert to min
            onChange(min);
            setInputValue(min.toString());
        } else if (numValue > max) {
            // If above max, revert to max
            onChange(max);
            setInputValue(max.toString());
        } else {
            // Valid value, update it
            onChange(numValue);
            setInputValue(numValue.toString());
        }
    };
    
    return (
        <div className={css({
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: '100%',
            opacity: disabled ? 0.6 : 1
        })}>
            <Button
                onClick={handleDecrement}
                disabled={disabled || value <= min}
                size={size}
                kind="outline"
                overrides={{
                    BaseButton: {
                        style: {
                            minWidth: '48px',
                            height: '36px',
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            lineHeight: '1',
                            paddingTop: '0',
                            paddingBottom: '0',
                            border: value <= min ? 'none' : `2px solid ${theme.colors.contentSecondary}`,
                            backgroundColor: 'transparent',
                            ':disabled': {
                                backgroundColor: 'transparent'
                            }
                        }
                    }
                }}
            >
                <span style={{ transform: 'translateY(-1px)', display: 'inline-block' }}>−</span>
            </Button>
            <Input
                type="number"
                value={inputValue}
                min={min}
                max={max}
                step={step}
                disabled={disabled}
                onChange={(e) => {
                    if (disabled) return;
                    // Allow free typing - don't clamp during input
                    setInputValue(e.target.value);
                }}
                onBlur={handleBlur}
                size={size}
                overrides={{
                    Input: {
                        style: {
                            width: '100%',
                            textAlign: 'center',
                            flex: 1,
                            MozAppearance: 'textfield',
                            '::-webkit-outer-spin-button': {
                                WebkitAppearance: 'none',
                                margin: 0
                            },
                            '::-webkit-inner-spin-button': {
                                WebkitAppearance: 'none',
                                margin: 0
                            }
                        }
                    }
                }}
            />
            <Button
                onClick={handleIncrement}
                disabled={disabled || value >= max}
                size={size}
                kind="outline"
                overrides={{
                    BaseButton: {
                        style: {
                            minWidth: '48px',
                            height: '36px',
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            lineHeight: '1',
                            paddingTop: '0',
                            paddingBottom: '0',
                            border: value >= max ? 'none' : `2px solid ${theme.colors.contentSecondary}`,
                            backgroundColor: 'transparent',
                            ':disabled': {
                                backgroundColor: 'transparent'
                            }
                        }
                    }
                }}
            >
                <span style={{ transform: 'translate(1px, -1px)', display: 'inline-block' }}>+</span>
            </Button>
        </div>
    );
}

export function Pricing() {

    useEnsurePaddleSetup();
    const [beginOrderTrigger] = useLazyCommand('C167v1_BeginOrder');
    const maintenance = window.LYW.maintenance === true;
    const trialMonthsDisplay = getSessionTrialDisplayMonths();
    
    //* view state
    const viewStates = {
        waiting: 1,
        showPlans: 2
    };
    const [viewState, setViewState] = React.useState(1);
    const [byoUnitPrices, setByoUnitByoUnitPrices] = React.useState();
    const [fixedPlans, setFixedPlans] = React.useState();
    const [currency, setCurrency] = React.useState();
    const [country, setCountry] = React.useState(window.LYW.PaddleCountryCode);
    const [taxMode, setTaxMode] = React.useState();
    const [isBuildYourOwnExpanded, setIsBuildYourOwnExpanded] = React.useState(false);

    //* styling 
    const [css, theme] = useStyletron();
    const formatDate = useFormatDisplayDate();

    //* auth status
    const {
        signUp,
    } = useLogin();
    const {
        authReady,
        authEnabled,
        isAuthenticated,
    } = useAuth("pricing.js");
    
    const [getProfile, gotProfile] = useLazyQuery('C166v1_GetUserProfile');

    const [getSubscriptions, gotSubscriptions] = useLazyQuery('C170v1_GetSubscriptionPlanDetails', {
        C170_CountryCode: country
    });

    useEffect(() => {
        if (authReady) {
            if (isAuthenticated) { //* user is logged in
                if (!gotProfile) {
                    getProfile();
                } else if (gotSubscriptions && gotProfile && viewState === viewStates.waiting) {
                    setPlansAndPrices();
                    if (gotProfile.e138_QueuedPlan.e138_PlanKey2 == 'custom') {
                        setByoStudentLogins([gotProfile.e138_QueuedPlan.e138_Credit2.e138_TotalStudentLogins || 1]);
                        const assessments = gotProfile.e138_QueuedPlan.e138_Credit2.e138_TotalAssessments || 30;
                        setByoAssessments([Math.max(10, assessments)]);
                        const lessonsAndActivities = gotProfile.e138_QueuedPlan.e138_Credit2.e138_TotalLessonsAndActivities || 100;
                        setByoLessonsAndActivities([Math.max(10, lessonsAndActivities)]);
                        setByoStudents([gotProfile.e138_QueuedPlan.e138_Credit2.e138_TotalStudents || 1]);
                        setByoTeacherLogins([gotProfile.e138_QueuedPlan.e138_Credit2.e138_TotalTeacherLogins || 1]);
                    }
                    // Initialize student counts from current subscription when on trial or paid plan
                    if (gotProfile.e138_QueuedPlan.e138_TrialKey === 'family' || 
                        (gotProfile.e138_QueuedPlan.e138_PlanKey2 === 'family' && !gotProfile.e138_QueuedPlan.e138_TrialKey)) {
                        setFamilyStudents([gotProfile.e138_QueuedPlan.e138_Credit2.e138_TotalStudents || 1]);
                    } else if (gotProfile.e138_QueuedPlan.e138_TrialKey === 'tutor' || 
                               (gotProfile.e138_QueuedPlan.e138_PlanKey2 === 'tutor' && !gotProfile.e138_QueuedPlan.e138_TrialKey)) {
                        setTutorStudents([gotProfile.e138_QueuedPlan.e138_Credit2.e138_TotalStudents || 10]);
                    }
                    setViewState(viewStates.showPlans);
                }
            } else if (gotSubscriptions && viewState === viewStates.waiting) {
                /* if your not logged in show all plans, this might mean a user that does have an account chooses the plan
                they already have if this is the case we will return them to the pricing page */
                setPlansAndPrices();
                setViewState(viewStates.showPlans);
            }
        }

        function setPlansAndPrices() {

            /* 
            on pricing page we should values based on detected location unless they are logged in and have an existing subscription
            then we use those details and in this case they should not be editable by the user     
             */
            const taxNotIncluded = "Excl. tax";
            setCurrencies(gotSubscriptions.e141_Pricing.e141_Currencies.allEnumerations.map(x => ({key: x.key, value: x.key})));
            setCurrency(gotSubscriptions.e141_Pricing.e141_BillingCurrency);
            setCountry(gotSubscriptions.e141_Pricing.e141_BillingCountry);
            setTaxMode(gotSubscriptions.e141_Pricing.e141_TaxMode === "external" ? taxNotIncluded : "");
            
            setByoUnitByoUnitPrices({
                students: gotSubscriptions.e141_Pricing.e141_PricePerStudent,
                teacherLogins: gotSubscriptions.e141_Pricing.e141_PricePerTeacherLogin,
                studentLogins: gotSubscriptions.e141_Pricing.e141_PricePerStudentLogin,
                assessments: gotSubscriptions.e141_Pricing.e141_PricePerAssessment,
                lessonsAndActivities: gotSubscriptions.e141_Pricing.e141_PricePerLessonsAndActivities,
                basePriceFamily: gotSubscriptions.e141_Pricing.e141_BasePriceFamily || 0,
                basePriceTutor: gotSubscriptions.e141_Pricing.e141_BasePriceTutor || 0,
                basePriceCustom: gotSubscriptions.e141_Pricing.e141_BasePriceCustom || 0
            });
            setFixedPlans({
                free: new Plan(gotSubscriptions.e141_Freebee),
                family: new Plan(gotSubscriptions.e141_Family),
                tutor: new Plan(gotSubscriptions.e141_Tutor)
            });
        }
    }, [authReady, gotProfile, gotSubscriptions]);

    class Plan {
        constructor(plan) {
            this.key = plan.e141_PlanKey;
            this.version = plan.e141_Version;
            this.title = plan.e141_PlanName;
            this.students = plan.e141_PlanCredit.e141_Students;
            this.assessments = plan.e141_PlanCredit.e141_Assessments;
            this.teacherLogins = plan.e141_PlanCredit.e141_TeacherLogins;
            this.studentLogins = plan.e141_PlanCredit.e141_StudentLogins;
            this.lessonsAndActivities = plan.e141_PlanCredit.e141_LessonsAndActivities;
            this.offersTrial = plan.e141_OffersTrial ?? false;
        }
    }

    const [byoStudents, setByoStudents] = React.useState([1]);
    const [byoTeacherLogins, setByoTeacherLogins] = React.useState([1]);
    const [byoStudentLogins, setByoStudentLogins] = React.useState([1]);
    const [byoAssessments, setByoAssessments] = React.useState([30]);
    const [byoLessonsAndActivities, setByoLessonsAndActivities] = React.useState([100]);
    const [familyStudents, setFamilyStudents] = React.useState([1]);
    const [tutorStudents, setTutorStudents] = React.useState([10]);

    const sliderOverrides = {
        InnerThumb: () => null,
        ThumbValue: () => null,
        TickBar: () => null
    };

    function calculateByoPlanPrice() {
        if (!byoUnitPrices) return "0";
        const price = (byoUnitPrices.basePriceCustom || 0) +
            byoUnitPrices.students * byoStudents[0] +
            byoUnitPrices.teacherLogins * Math.max(0, byoTeacherLogins[0] - 1) +
            byoUnitPrices.studentLogins * byoStudentLogins[0] +
            byoUnitPrices.assessments * byoAssessments[0] +
            byoUnitPrices.lessonsAndActivities * byoLessonsAndActivities[0];
        return toMoney(price);
    }

    function calculateFamilyPlanPrice() {
        if (!byoUnitPrices || !fixedPlans?.family) return "0";
        const studentCount = familyStudents[0];
        const assessments = studentCount * 30;
        const lessonsAndActivities = studentCount * 100;
        const price = (byoUnitPrices.basePriceFamily || 0) +
            byoUnitPrices.students * studentCount +
            byoUnitPrices.teacherLogins * 1 + // 1 additional (1 included free)
            byoUnitPrices.studentLogins * studentCount +
            byoUnitPrices.assessments * assessments +
            byoUnitPrices.lessonsAndActivities * lessonsAndActivities;
        return toMoney(price);
    }

    function calculateTutorPlanPrice() {
        if (!byoUnitPrices || !fixedPlans?.tutor) return "0";
        const studentCount = tutorStudents[0];
        const assessments = studentCount * 10;
        const lessonsAndActivities = studentCount * 30;
        const price = (byoUnitPrices.basePriceTutor || 0) +
            byoUnitPrices.students * studentCount +
            byoUnitPrices.teacherLogins * 0 + // 0 additional (1 included free, fixed)
            byoUnitPrices.assessments * assessments +
            byoUnitPrices.lessonsAndActivities * lessonsAndActivities;
        return toMoney(price);
    }

    function getCurrencySymbol(currencyCode) {
        switch (currencyCode) {
            case 'GBP': return '£';
            case 'USD': return '$';
            case 'EUR': return '€';
            default: return currencyCode; // fallback to code if unknown
        }
    }

    function onBuy(planKey) {
        //* start the stateful order process
        const c167 = {
            C167_PlanKey: planKey,
            C167_Country: country,
            C167_ChosenCurrency: currency
        };
        if (planKey === "custom") {
            c167.C167_ByoStudents = byoStudents[0];
            c167.C167_ByoTeacherLogins = byoTeacherLogins[0];
            c167.C167_ByoStudentLogins = byoStudentLogins[0];
            c167.C167_ByoAssessments = byoAssessments[0];
            c167.C167_ByoLessonsAndActivities = byoLessonsAndActivities[0];
        } else if (planKey === "family") {
            c167.C167_FamilyStudents = familyStudents[0];
        } else if (planKey === "tutor") {
            c167.C167_TutorStudents = tutorStudents[0];
        }
        beginOrderTrigger(appendTrialPromoToBeginOrder(c167));
        setViewState(viewStates.waiting);
    }

    useEvent({
        eventName: "E139v1_OrderProcessStarted",
        onEventReceived: (event) => {
            try {
                const statefulProcessId = getHeader(event, headerKeys.statefulProcessId);
                if (!statefulProcessId) {
                    console.error("E139v1_OrderProcessStarted: missing statefulProcessId header", event);
                    return;
                }
                const orderPageUrl = window.location.origin + '/#/order/' + window.btoa(statefulProcessId);
                if (isAuthenticated) {
                    window.location.assign(orderPageUrl);
                } else {
                    signUp(orderPageUrl);
                }
            } catch (err) {
                console.error("E139v1_OrderProcessStarted callback error", err);
            }
        }
    });

    const currentPlanKey = gotProfile?.e138_QueuedPlan.e138_PlanKey2;
    const isOnTrial = gotProfile?.e138_QueuedPlan.e138_TrialKey != null;
    const trialPlanKey = gotProfile?.e138_QueuedPlan.e138_TrialKey;
    const isOnNormalFreePlan = gotProfile?.e138_IsOnNormalFreePlan ?? false;
    const hasHadTrial = gotProfile?.e138_HasHadTrial ?? false;
    const isOnPlanWithSubAccounts = gotProfile?.e138_IsOnAPlanWithSubAccounts;
    const orgRoleKey = gotProfile?.e138_RoleKeys.find(x => x === Roles.OrgUser || x === Roles.OrgAdmin);
    
    // Helper variables to check trial status (using backend-provided trial key)
    const isOnFamilyTrial = isOnTrial && trialPlanKey === fixedPlans?.family?.key;
    const isOnTutorTrial = isOnTrial && trialPlanKey === fixedPlans?.tutor?.key;
    const [currencies, setCurrencies] = useState([]);
    const existingBilling = isAuthenticated && gotSubscriptions?.e141_Pricing.e141_HasExistingSubscription;

    // Helper function to get current student count for a plan
    function getCurrentStudentCount(planKey) {
        if (!gotProfile) return null;
        if (planKey === 'family' && (currentPlanKey === 'family' || trialPlanKey === 'family')) {
            return gotProfile.e138_QueuedPlan.e138_Credit2.e138_TotalStudents || 1;
        }
        if (planKey === 'tutor' && (currentPlanKey === 'tutor' || trialPlanKey === 'tutor')) {
            return gotProfile.e138_QueuedPlan.e138_Credit2.e138_TotalStudents || 10;
        }
        return null;
    }

    // Helper function to check if student count differs from current plan
    function studentCountDiffers(planKey) {
        if (!gotProfile || isOnTrial) return false;
        const currentCount = getCurrentStudentCount(planKey);
        if (currentCount === null) return false;
        
        if (planKey === 'family') {
            return familyStudents[0] !== currentCount;
        }
        if (planKey === 'tutor') {
            return tutorStudents[0] !== currentCount;
        }
        return false;
    }

    // Helper function to check if custom plan amounts differ from current plan
    function customPlanAmountsDiffer() {
        if (!gotProfile || isOnTrial || currentPlanKey !== 'custom') return false;
        const currentCredit = gotProfile.e138_QueuedPlan.e138_Credit2;
        return (
            byoStudents[0] !== (currentCredit.e138_TotalStudents || 1) ||
            byoTeacherLogins[0] !== (currentCredit.e138_TotalTeacherLogins || 1) ||
            byoStudentLogins[0] !== (currentCredit.e138_TotalStudentLogins || 1) ||
            byoAssessments[0] !== (currentCredit.e138_TotalAssessments || 30) ||
            byoLessonsAndActivities[0] !== (currentCredit.e138_TotalLessonsAndActivities || 100)
        );
    }

    function isCurrentPlan(planKey) {
        // Check if this is actually the user's current plan (not just matching plan key, which could be a trial)
        // When on a trial, it's not considered "Your Plan" - it's still a trial that needs to be converted
        // For family/tutor plans, also check that student count matches
        // For custom plan, also check that amounts match
        if (planKey === "free") {
            return (currentPlanKey === planKey && isOnNormalFreePlan);
        }
        if (planKey === "family" || planKey === "tutor") {
            const planMatches = currentPlanKey === planKey && !isOnTrial;
            if (!planMatches) return false;
            // If plan matches, check if student count also matches
            return !studentCountDiffers(planKey);
        }
        if (planKey === "custom") {
            const planMatches = currentPlanKey === planKey && !isOnTrial;
            if (!planMatches) return false;
            // If plan matches, check if amounts also match
            return !customPlanAmountsDiffer();
        }
        return (currentPlanKey === planKey && !isOnTrial);
    }

    function getButtonText(planKey) {
        // Check if this is actually the user's current plan (not just matching plan key, which could be a trial)
        if (isCurrentPlan(planKey)) {
            return "Your Plan";
        }
        
        // Check if user is on same plan type but with different student count (family/tutor only)
        if (!isOnTrial && gotProfile && (planKey === 'family' || planKey === 'tutor')) {
            const planMatches = currentPlanKey === planKey;
            if (planMatches && studentCountDiffers(planKey)) {
                return "Change Plan";
            }
        }
        
        // Check if user is on custom plan but with different amounts
        if (!isOnTrial && gotProfile && planKey === 'custom' && currentPlanKey === 'custom' && customPlanAmountsDiffer()) {
            return "Change Plan";
        }
        
        const planOffersTrial = fixedPlans?.[planKey]?.offersTrial ?? false;
        if (!gotProfile) {
            // Not logged in - show "Begin Free Trial" for plans that offer trials, "Buy Now" for others
            if (planOffersTrial) {
                return "Begin Free Trial";
            }
            return "Buy Now";
        }
        // Logged in
        if (isOnTrial) {
            // Can only convert to same plan as trial
            if (planOffersTrial && planKey === trialPlanKey) {
                return "Keep This Plan";  // Triggers conversion flow
            }
            
            // Free plan always switchable
            if (planKey === "free") {
                return "Switch To This Plan";
            }
            
            // Custom plan shows warning about immediate activation
            if (planKey === "custom") {
                return "Switch To This Plan";
            }
            
            // Other trial plans disabled
            return "Keep This Plan";
        }
        // If user has already used their free trial, show "Switch To This Plan" instead of "Begin Free Trial"
        if (isOnNormalFreePlan && planOffersTrial && !hasHadTrial) {
            return "Begin Free Trial";
        }
        return "Switch To This Plan";
    }
    
    function isPlanDisabled(planKey) {
        // Allow plan selection if student count differs (for family/tutor plans)
        if (!isOnTrial && gotProfile && (planKey === 'family' || planKey === 'tutor')) {
            const planMatches = currentPlanKey === planKey;
            if (planMatches && studentCountDiffers(planKey)) {
                return false; // Allow when student count differs
            }
        }
        // Allow changing custom plan amounts when already on custom and amounts differ
        if (!isOnTrial && gotProfile && planKey === 'custom' && currentPlanKey === 'custom' && customPlanAmountsDiffer()) {
            return false; // Allow when amounts differ
        }
        
        if (isCurrentPlan(planKey)) {
            return true;
        }
        if (maintenance) {
            return true;
        }
        
        // Handle trial state - cannot switch between trial plans
        if (isOnTrial) {
            const planOffersTrial = fixedPlans?.[planKey]?.offersTrial ?? false;
            
            // When on trial: only allow same trial plan (to convert), free, or custom
            // Disable switching between tutor and family trials
            if (planOffersTrial && planKey !== trialPlanKey) {
                return true;  // Disable other trial plans (can't switch tutor<->family during trial)
            }
        }
        
        // OrgAdmins can switch to any plan at any time
        // Non-admins cannot change plans when sub-accounts are enabled
        if (isOnPlanWithSubAccounts && orgRoleKey !== Roles.OrgAdmin) {
            return true;  // Non-admins blocked from all plan changes
        }
        
        return false;
    }
    
    function calculateRemainingTrialDays(trialExpiry) {
        if (!trialExpiry) return 0;
        const expiryDate = new Date(trialExpiry);
        const today = new Date();
        const diffTime = expiryDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return Math.max(0, diffDays);
    }
    
    
    
    switch (viewState) {
        case viewStates.waiting:
            return <CenterSpinner/>;
        case viewStates.showPlans:
            return (
                <Frame>
                    <div className={css({
                        minHeight: '100vh',
                        background: 'linear-gradient(to bottom right, white, rgb(248 250 252))',
                        paddingTop: '32px',
                        paddingBottom: '32px',
                        paddingLeft: '24px',
                        paddingRight: '24px',
                        '@media (min-width: 640px)': {
                            paddingLeft: '48px',
                            paddingRight: '48px'
                        },
                        '@media (min-width: 1316px)': {
                            paddingLeft: '96px',
                            paddingRight: '96px'
                        }
                    })}>
                        <div className="max-w-7xl mx-auto">
                            {/* Top Section - Header and Currency Panel */}
                            <div className="relative mb-6">
                                {/* Currency Panel - Desktop */}
                                <CurrencySelector
                                    variant="desktop"
                                    currency={currency}
                                    currencies={currencies}
                                    existingBilling={existingBilling}
                                    onChange={params => {
                                        const currencyCode = params?.value[0]?.key;
                                        setViewState(viewStates.waiting);
                                        getSubscriptions({
                                            C170_CountryCode: country,
                                            C170_CurrencyCode: currencyCode
                                        });
                                    }}
                                />
                                
                                {/* Centered Header Content */}
                                <div className="text-center">
                                    <h1 className="text-4xl md:text-5xl font-extrabold text-[#1c5375] mb-4">Explore Your Learning Options</h1>
                                    <p className="text-[#656565] text-xl leading-relaxed mb-4 max-w-2xl mx-auto">
                                        Try it free for {trialMonthsDisplay} months. No credit card. No auto-billing.
                                    </p>
                                    <p className="text-[#656565] text-lg mb-4">
                                        We believe families need time to see real learning.
                                        That's why we don't rush the decision.
                                    </p>
                                </div>
                                
                                {/* Currency Panel - Mobile */}
                                <CurrencySelector
                                    variant="mobile"
                                    currency={currency}
                                    currencies={currencies}
                                    existingBilling={existingBilling}
                                    onChange={params => {
                                        const currencyCode = params?.value[0]?.key;
                                        setViewState(viewStates.waiting);
                                        getSubscriptions({
                                            C170_CountryCode: country,
                                            C170_CurrencyCode: currencyCode
                                        });
                                    }}
                                />
                            </div>
                                
                            {/* Organization User Notice - Keep original logic */}
                            {orgRoleKey === Roles.OrgUser ?
                                <div className={css({
                                    display: 'flex',
                                    marginTop:"9px",
                                    justifyContent: 'space-evenly',
                                    padding: "12px",
                                    backgroundColor: theme.colors.accent600,
                                    borderRadius: "6px",
                                    flexWrap: 'wrap'
                                })}><ParagraphSmall>Speak to your Organisation Admin to change the subscription plan</ParagraphSmall></div>
                                : null}
                            {/* Pricing Plans Grid */}
                            <div className={css({ display: 'flex', justifyContent: 'center', paddingTop: '8px', paddingLeft: '16px', paddingRight: '16px', paddingBottom: '16px' })}>
                                <div className={css({
                                    display: 'grid',
                                    gap: '24px',
                                    gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
                                    justifyItems: 'center',
                                    '@media (min-width: 768px)': {
                                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                                        justifyItems: 'center'
                                    },
                                    '@media (min-width: 1316px)': {
                                        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                                        justifyItems: 'center'
                                    },
                                    maxWidth: '1280px',
                                    width: '100%',
                                    alignItems: 'stretch'
                                })}>
                                
                                <PricingPane title={fixedPlans.free.title} reduceTitleSpacing={true} reduceBottomSpacing={true}>
                                    <PlanImage src={starterAccess} alt="Children engaged in learning at desk or tablet"/>
                                    <LineItem noDivider><Centered><LabelSmall style={{fontSize: '18px', fontWeight: 600}}>Free Forever</LabelSmall></Centered></LineItem>
                                    <ActionButton
                                        text={!gotProfile ? "Get Started" : getButtonText(fixedPlans.free.key)}
                                        disabled={isPlanDisabled(fixedPlans.free.key)}
                                        onClick={() => onBuy(fixedPlans.free.key)}/>
                                    <div className={css({marginBottom: '-20px'})}>
                                        <ThinLineItem noDivider>{(fixedPlans.free.students > 1 ? 'Up to ' : '')}<strong>{fixedPlans.free.students}</strong> {pluralize(fixedPlans.free.students, 'student', 'students')}</ThinLineItem>
                                        <ThinLineItem noDivider>{(fixedPlans.free.teacherLogins > 1 ? 'Up to ' : '')}<strong>{fixedPlans.free.teacherLogins}</strong> {pluralize(fixedPlans.free.teacherLogins, 'parent/tutor login', 'parent/tutor logins')}</ThinLineItem>
                                        <ThinLineItem noDivider>{(fixedPlans.free.studentLogins > 1 ? 'Up to ' : '')}<strong>{fixedPlans.free.studentLogins}</strong> {pluralize(fixedPlans.free.studentLogins, 'student login', 'student logins')}</ThinLineItem>
                                        <ThinLineItem noDivider>{(fixedPlans.free.assessments > 1 ? 'Up to ' : '')}<strong>{fixedPlans.free.assessments}</strong> {pluralize(fixedPlans.free.assessments, 'assessment', 'assessments')} per month</ThinLineItem>
                                        <ThinLineItem noDivider>{(fixedPlans.free.lessonsAndActivities > 1 ? 'Up to ' : '')}<strong>{fixedPlans.free.lessonsAndActivities}</strong> {pluralize(fixedPlans.free.lessonsAndActivities, 'lesson and activity', 'lessons and activities')} per month</ThinLineItem>
                                    </div>
                                </PricingPane>
                                <PricingPane 
                                    title={<>{fixedPlans.family.title}</>} 
                                    reduceTitleSpacing={true} 
                                    reduceBottomSpacing={true}
                                    overlay={isOnTutorTrial ? (
                                        <div className={css({
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                            zIndex: 10,
                                            pointerEvents: 'none',
                                            borderRadius: '8px'
                                        })} />
                                    ) : null}
                                >
                                        <div>
                                        <PlanImage src={familyPlan} alt="Parent guiding child at screen"/>
                                        {!gotProfile && fixedPlans.family.offersTrial && (
                                            <div className={css({textAlign: 'center', marginTop: '16px', marginBottom: '4px'})}>
                                                <div className={css({marginBottom: '4px'})}>
                                                    <LabelSmall style={{fontWeight: 'bold', color: theme.colors.primary}}>Start with {trialMonthsDisplay} months free</LabelSmall>
                                                </div>
                                                <LabelXSmall style={{color: theme.colors.contentSecondary}}>You won't be asked to pay until you decide to continue.</LabelXSmall>
                                            </div>
                                        )}
                                        {gotProfile && isOnNormalFreePlan && !hasHadTrial && fixedPlans.family.offersTrial && (
                                            <div className={css({textAlign: 'center', marginTop: '16px', marginBottom: '4px'})}>
                                                <div className={css({marginBottom: '4px'})}>
                                                    <LabelSmall style={{fontWeight: 'bold', color: theme.colors.primary}}>Start with {trialMonthsDisplay} months free</LabelSmall>
                                                </div>
                                                <LabelXSmall style={{color: theme.colors.contentSecondary}}>You won't be asked to pay until you decide to continue.</LabelXSmall>
                                            </div>
                                        )}
                                        {isOnFamilyTrial && (
                                            <div className={css({textAlign: 'center', marginTop: '16px', marginBottom: '8px', color: theme.colors.contentSecondary})}>
                                                <LabelSmall>You are on a free trial.<br/>{calculateRemainingTrialDays(gotProfile?.e138_QueuedPlan.e138_TrialExpiry)} days left.</LabelSmall>
                                            </div>
                                        )}
                                        {isOnTutorTrial ? (
                                            <div className={css({textAlign: 'center', marginTop: isOnFamilyTrial ? '48px' : '16px', marginBottom: '32px', color: theme.colors.contentSecondary})}>
                                                <LabelSmall>You cannot switch to a trial of this plan while trialing another plan</LabelSmall>
                                            </div>
                                        ) : (
                                            <>
                                                {gotProfile?.e138_QueuedPlan.e138_TrialPaymentDetailsProvided && isOnFamilyTrial ? (
                                                    <div className={css({textAlign: 'center', marginTop: '8px', marginBottom: '8px', padding: '12px', backgroundColor: theme.colors.backgroundSecondary, borderRadius: '8px'})}>
                                                        <LabelXSmall style={{color: theme.colors.contentSecondary}}>
                                                            Payment details have been provided. Billing will begin on{" "}
                                                            {gotProfile?.e138_QueuedPlan.e138_TrialExpiry
                                                                ? formatDate(gotProfile.e138_QueuedPlan.e138_TrialExpiry)
                                                                : 'the end of your trial'}
                                                            .
                                                        </LabelXSmall>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <ActionButton
                                                            text={getButtonText(fixedPlans.family.key)}
                                                            disabled={isPlanDisabled(fixedPlans.family.key)}
                                                            onClick={() => onBuy(fixedPlans.family.key)}/>
                                                        {isOnFamilyTrial && (
                                                            <LabelXSmall style={{textAlign: 'center', color: theme.colors.contentSecondary, marginTop: '-16px', marginBottom: '8px'}}>after the trial ends</LabelXSmall>
                                                        )}
                                                        <LineItem noDivider><Centered>
                                                            <div className={css({display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px'})}>
                                                                <LabelSmall style={{textAlign: 'center'}}>{getCurrencySymbol(currency)}{calculateFamilyPlanPrice()} /month</LabelSmall>
                                                                {isOnFamilyTrial && (
                                                                    <InfoTooltip />
                                                                )}
                                                            </div>
                                                            <LabelXSmall style={{textAlign: 'center'}}>{taxMode}</LabelXSmall>
                                                            {fixedPlans.family.offersTrial && !hasHadTrial && (
                                                                <LabelXSmall style={{textAlign: 'center', color: theme.colors.contentSecondary}}>if you decide to continue</LabelXSmall>
                                                            )}
                                                        </Centered></LineItem>
                                                    </>
                                                )}
                                            </>
                                        )}
                                        <LineItem noDivider>
                                            <strong>{familyStudents[0]}</strong> {pluralize(familyStudents[0], 'student', 'students')}<br/>
                                                <div className={css({marginTop: '8px'})}>
                                                    <NumberInputWithButtons
                                                        value={familyStudents[0]}
                                                        min={1}
                                                        max={10}
                                                        step={1}
                                                        disabled={isOnTrial}
                                                        onChange={(val) => setFamilyStudents([val])}
                                                    />
                                                </div>
                                                {isOnFamilyTrial && (
                                                    <LabelXSmall style={{color: theme.colors.contentSecondary, marginTop: '4px', display: 'block'}}>
                                                        Student count cannot be changed during trial
                                                    </LabelXSmall>
                                                )}
                                        </LineItem>
                                        <div className={css({marginTop: '16px', marginBottom: '-20px'})}>
                                            <ThinLineItem noDivider>{(fixedPlans.family.teacherLogins > 1 ? 'Up to ' : '')}<strong>{fixedPlans.family.teacherLogins}</strong> {pluralize(fixedPlans.family.teacherLogins, 'parent login', 'parent logins')}</ThinLineItem>
                                            <ThinLineItem noDivider>{(familyStudents[0] > 1 ? 'Up to ' : '')}<strong>{familyStudents[0]}</strong> {pluralize(familyStudents[0], 'student login', 'student logins')}</ThinLineItem>
                                            <ThinLineItem noDivider>{(familyStudents[0] * 30 > 1 ? 'Up to ' : '')}<strong>{familyStudents[0] * 30}</strong> {pluralize(familyStudents[0] * 30, 'assessment', 'assessments')} per month</ThinLineItem>
                                            <ThinLineItem noDivider>{(familyStudents[0] * 100 > 1 ? 'Up to ' : '')}<strong>{familyStudents[0] * 100}</strong> {pluralize(familyStudents[0] * 100, 'lesson and activity', 'lessons and activities')} per month</ThinLineItem>
                                        </div>
                                        </div>
                                </PricingPane>
                                    
                                <PricingPane 
                                    title={<>{fixedPlans.tutor.title}</>} 
                                    reduceTitleSpacing={true} 
                                    reduceBottomSpacing={true}
                                    overlay={isOnFamilyTrial ? (
                                        <div className={css({
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                            zIndex: 10,
                                            pointerEvents: 'none',
                                            borderRadius: '8px'
                                        })} />
                                    ) : null}
                                >
                                        <div>
                                        <div className={css({ display: 'flex', justifyContent: 'center' })}>
                                            <img className={css({ borderRadius: '12px' })} width={"85%"} height={"85%"} src={tutorPlan} alt="Tutor in small-group setting"/>
                                        </div>

                                        {!gotProfile && fixedPlans.tutor.offersTrial && (
                                            <div className={css({textAlign: 'center', marginTop: '16px', marginBottom: '4px'})}>
                                                <div className={css({marginBottom: '4px'})}>
                                                    <LabelSmall style={{fontWeight: 'bold', color: theme.colors.primary}}>Start with {trialMonthsDisplay} months free</LabelSmall>
                                                </div>
                                                <LabelXSmall style={{color: theme.colors.contentSecondary}}>You won't be asked to pay until you decide to continue.</LabelXSmall>
                                            </div>
                                        )}
                                        {gotProfile && isOnNormalFreePlan && !hasHadTrial && fixedPlans.tutor.offersTrial && (
                                            <div className={css({textAlign: 'center', marginTop: '16px', marginBottom: '4px'})}>
                                                <div className={css({marginBottom: '4px'})}>
                                                    <LabelSmall style={{fontWeight: 'bold', color: theme.colors.primary}}>Start with {trialMonthsDisplay} months free</LabelSmall>
                                                </div>
                                                <LabelXSmall style={{color: theme.colors.contentSecondary}}>You won't be asked to pay until you decide to continue.</LabelXSmall>
                                            </div>
                                        )}
                                        {isOnTutorTrial && (
                                            <div className={css({textAlign: 'center', marginTop: '16px', marginBottom: '8px', color: theme.colors.contentSecondary})}>
                                                <LabelSmall>You are on a free trial.<br/>{calculateRemainingTrialDays(gotProfile?.e138_QueuedPlan.e138_TrialExpiry)} days left.</LabelSmall>
                                            </div>
                                        )}
                                        {isOnFamilyTrial ? (
                                            <div className={css({textAlign: 'center', marginTop: isOnTutorTrial ? '64px' : '16px', marginBottom: '32px', color: theme.colors.contentSecondary})}>
                                                <LabelSmall>You cannot switch to a trial of this plan while trialing another plan</LabelSmall>
                                            </div>
                                        ) : (
                                            <>
                                                {gotProfile?.e138_QueuedPlan.e138_TrialPaymentDetailsProvided && isOnTutorTrial ? (
                                                    <div className={css({textAlign: 'center', marginTop: '8px', marginBottom: '8px', padding: '12px', backgroundColor: theme.colors.backgroundSecondary, borderRadius: '8px'})}>
                                                        <LabelXSmall style={{color: theme.colors.contentSecondary}}>
                                                            Payment details have been provided. Billing will begin on{" "}
                                                            {gotProfile?.e138_QueuedPlan.e138_TrialExpiry
                                                                ? formatDate(gotProfile.e138_QueuedPlan.e138_TrialExpiry)
                                                                : 'the end of your trial'}
                                                            .
                                                        </LabelXSmall>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <ActionButton
                                                            text={getButtonText(fixedPlans.tutor.key)}
                                                            disabled={isPlanDisabled(fixedPlans.tutor.key)}
                                                            onClick={() => onBuy(fixedPlans.tutor.key)}/>
                                                        {isOnTutorTrial && (
                                                            <LabelXSmall style={{textAlign: 'center', color: theme.colors.contentSecondary, marginTop: '-16px', marginBottom: '8px'}}>after the trial ends</LabelXSmall>
                                                        )}
                                                        <LineItem noDivider><Centered>
                                                            <div className={css({display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px'})}>
                                                                <LabelSmall style={{textAlign: 'center'}}>{getCurrencySymbol(currency)}{calculateTutorPlanPrice()} /month</LabelSmall>
                                                                {isOnTutorTrial && (
                                                                    <InfoTooltip />
                                                                )}
                                                            </div>
                                                            <LabelXSmall style={{textAlign: 'center'}}>{taxMode}</LabelXSmall>
                                                            {fixedPlans.tutor.offersTrial && !hasHadTrial && (
                                                                <LabelXSmall style={{textAlign: 'center', color: theme.colors.contentSecondary}}>if you decide to continue</LabelXSmall>
                                                            )}
                                                        </Centered></LineItem>
                                                    </>
                                                )}
                                            </>
                                        )}
                                        <LineItem noDivider>
                                            Up to <strong>{tutorStudents[0]}</strong> {pluralize(tutorStudents[0], 'student', 'students')}<br/>
                                            <div className={css({marginTop: '8px'})}>
                                                <NumberInputWithButtons
                                                    value={tutorStudents[0]}
                                                    min={10}
                                                    max={100}
                                                    step={5}
                                                    disabled={isOnTrial}
                                                    onChange={(val) => setTutorStudents([val])}
                                                />
                                            </div>
                                            {isOnTutorTrial && (
                                                <LabelXSmall style={{color: theme.colors.contentSecondary, marginTop: '4px', display: 'block'}}>
                                                    Student count cannot be changed during trial
                                                </LabelXSmall>
                                            )}
                                        </LineItem>
                                        <div className={css({marginTop: '16px', marginBottom: '-20px'})}>
                                            <ThinLineItem noDivider>{(fixedPlans.tutor.teacherLogins > 1 ? 'Up to ' : '')}<strong>{fixedPlans.tutor.teacherLogins}</strong> {pluralize(fixedPlans.tutor.teacherLogins, 'tutor login', 'tutor logins')}</ThinLineItem>
                                            <ThinLineItem noDivider>{(fixedPlans.tutor.studentLogins > 1 ? 'Up to ' : '')}<strong>{fixedPlans.tutor.studentLogins}</strong> {pluralize(fixedPlans.tutor.studentLogins, 'student login', 'student logins')}</ThinLineItem>
                                            <ThinLineItem noDivider>{(tutorStudents[0] * 10 > 1 ? 'Up to ' : '')}<strong>{tutorStudents[0] * 10}</strong> {pluralize(tutorStudents[0] * 10, 'assessment', 'assessments')} per month</ThinLineItem>
                                            <ThinLineItem noDivider>{(tutorStudents[0] * 30 > 1 ? 'Up to ' : '')}<strong>{tutorStudents[0] * 30}</strong> {pluralize(tutorStudents[0] * 30, 'lesson and activity', 'lessons and activities')} per month</ThinLineItem>
                                        </div>
                                        </div>
                                </PricingPane>
                                <PricingPane title={"Custom Package"} reduceTitleSpacing={true} reduceBottomSpacing={true}>
                                    <PlanImage src={buildYourOwn} alt="Custom learning plan"/>
                                    <div className={css({ paddingBottom: 0 })}>
                                        <ActionButton
                                            disabled={isPlanDisabled("custom")}
                                            text={getButtonText("custom")}
                                            onClick={() => onBuy("custom")}/>
                                        <LineItem noDivider>
                                            <Centered>
                                                <div className={css({ marginTop: '-12px' })}>
                                                    <Button
                                                        kind="minimal"
                                                        onClick={() => setIsBuildYourOwnExpanded(!isBuildYourOwnExpanded)}
                                                        overrides={{
                                                            BaseButton: {
                                                                style: {
                                                                    fontSize: '15px',
                                                                    fontWeight: 500,
                                                                    color: theme.colors.contentPrimary,
                                                                    paddingTop: '8px',
                                                                    paddingBottom: '8px',
                                                                    paddingLeft: '12px',
                                                                    paddingRight: '12px',
                                                                    ':hover': {
                                                                        backgroundColor: theme.colors.mono100
                                                                    }
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        {isBuildYourOwnExpanded ? 'Hide Details' : 'Show Details'}
                                                        <ChevronDown 
                                                            size={16}
                                                            overrides={{
                                                                Svg: {
                                                                    style: {
                                                                        transform: isBuildYourOwnExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                                                        transition: 'transform 0.2s',
                                                                        marginLeft: '6px'
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                    </Button>
                                                </div>
                                            </Centered>
                                        </LineItem>
                                        {isBuildYourOwnExpanded && (
                                            <>
                                        <LineItem
                                            noDivider><Centered><div className={css({ marginTop: '-8px' })}><LabelSmall style={{fontSize: '16px'}}>{getCurrencySymbol(currency)}{calculateByoPlanPrice()} /mo.<LabelXSmall>{taxMode}</LabelXSmall></LabelSmall></div></Centered></LineItem>
                                        <LineItem noDivider>
                                            <strong>{byoStudents[0]}</strong> {pluralize(byoStudents[0], 'student', 'students')}<br/>
                                            <div className={css({marginTop: '8px'})}>
                                                <NumberInputWithButtons
                                                    value={byoStudents[0]}
                                                    min={1}
                                                    max={1000}
                                                    step={1}
                                                    onChange={(val) => {
                                                        setByoStudents([val]);
                                                        // If students is decreased below student logins, clamp student logins
                                                        if (val < byoStudentLogins[0]) {
                                                            setByoStudentLogins([val]);
                                                        }
                                                    }}
                                                />
                                            </div>
                                            <PriceDisplay price={byoUnitPrices?.students} currency={currency} />
                                        </LineItem>
                                        <LineItem noDivider>
                                            <strong>{byoTeacherLogins[0]}</strong> {pluralize(byoTeacherLogins[0], 'parent/tutor login', 'parent/tutor logins')}<br/>
                                            <div className={css({marginTop: '8px'})}>
                                                <NumberInputWithButtons
                                                    value={byoTeacherLogins[0]}
                                                    min={1}
                                                    max={50}
                                                    step={1}
                                                    onChange={(val) => setByoTeacherLogins([val])}
                                                />
                                            </div>
                                            <PriceDisplay price={byoUnitPrices?.teacherLogins} currency={currency} />
                                        </LineItem>
                                        <LineItem noDivider>
                                            <strong>{byoStudentLogins[0]}</strong> {pluralize(byoStudentLogins[0], 'student login', 'student logins')} (optional)<br/>
                                            <div className={css({marginTop: '8px'})}>
                                                <NumberInputWithButtons
                                                    value={byoStudentLogins[0]}
                                                    min={0}
                                                    max={1000}
                                                    step={1}
                                                    onChange={(val) => {
                                                        // If student logins exceeds students, increase students to match
                                                        if (val > byoStudents[0]) {
                                                            setByoStudents([val]);
                                                        }
                                                        setByoStudentLogins([val]);
                                                    }}
                                                />
                                            </div>
                                            <PriceDisplay price={byoUnitPrices?.studentLogins} currency={currency} />
                                        </LineItem>
                                        <LineItem noDivider>
                                            <strong>{byoAssessments[0]}</strong> {pluralize(byoAssessments[0], 'assessment', 'assessments')} per month<br/>
                                            <div className={css({marginTop: '8px'})}>
                                                <NumberInputWithButtons
                                                    value={byoAssessments[0]}
                                                    min={10}
                                                    max={10000}
                                                    step={10}
                                                    onChange={(val) => setByoAssessments([val])}
                                                />
                                            </div>
                                            <PriceDisplay price={byoUnitPrices?.assessments} currency={currency} />
                                        </LineItem>
                                        <LineItem noDivider>
                                            <div className={css({ marginBottom: 0 })}>
                                                <strong>{byoLessonsAndActivities[0]}</strong> {pluralize(byoLessonsAndActivities[0], 'lesson and activity', 'lessons and activities')} per month<br/>
                                                <div className={css({marginTop: '8px'})}>
                                                    <NumberInputWithButtons
                                                        value={byoLessonsAndActivities[0]}
                                                        min={10}
                                                        max={10000}
                                                        step={10}
                                                        onChange={(val) => setByoLessonsAndActivities([val])}
                                                    />
                                                </div>
                                                <PriceDisplay price={byoUnitPrices?.lessonsAndActivities} currency={currency} />
                                            </div>
                                        </LineItem>
                                            </>
                                        )}
                                    </div>
                                </PricingPane>
                                
                                </div>
                            </div>
                            
                            {/* Footer Note */}
                            <div className="text-center mt-8">
                                <p className="text-[#656565] text-lg italic">
                                    As parents and educators, we know choosing learning tools takes time.
                                    That's why you can try before you buy.
                                </p>
                            </div>
                        </div>
                    </div>
                </Frame>
            );
    }


}